import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import { cn } from '@/lib/utils'

interface LabelBadgeProps {
  label: LabelDocument
  className?: string
  size?: 'sm' | 'md'
  showRemove?: boolean // Added for backwards compatibility
}

/**
 * LabelBadge - Displays a label with its color
 *
 * @example
 * <LabelBadge label={label} />
 */
export function LabelBadge({
  label,
  className,
  size = 'sm',
  showRemove: _showRemove, // Accept but ignore for now
}: LabelBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-input bg-card px-2.5 text-foreground font-medium transition-colors hover:border-border hover:bg-secondary',
        size === 'sm' ? 'py-0.5 text-xs' : 'py-1 text-sm',
        className,
      )}
    >
      {/* Color dot indicator */}
      <span
        className="size-2 rounded-full shrink-0"
        style={{ backgroundColor: label.color }}
      />

      {/* Label name */}
      <span className="truncate">{label.name}</span>
    </span>
  )
}

/**
 * LabelList - Displays a horizontal list of label badges
 */
interface LabelListProps {
  labels: Array<LabelDocument>
  className?: string
  maxVisible?: number
  showRemove?: boolean // Kept for backwards compatibility, but ignored
}

export function LabelList({ labels, className, maxVisible }: LabelListProps) {
  const visibleLabels = maxVisible ? labels.slice(0, maxVisible) : labels
  const hiddenCount = maxVisible ? Math.max(0, labels.length - maxVisible) : 0

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {visibleLabels.map((label) => (
        <LabelBadge key={label.id} label={label} />
      ))}
      {hiddenCount > 0 && (
        <span className="text-xs text-muted-foreground">
          +{hiddenCount} more
        </span>
      )}
    </div>
  )
}
